import React, {FC, useState} from "react";
import {Trans} from "@lingui/macro";
import {Breadcrumb, Button, Card, Space} from "antd";
import {DeleteOutlined, FileExcelOutlined, MailOutlined, UploadOutlined} from "@ant-design/icons";
import {useKeycloak} from "@react-keycloak/web";
import {openNotification} from "../../utils/openNotification";
import {useAxios} from "../../utils/hooks";
import Dragger from "antd/es/upload/Dragger";
import Title from "antd/es/skeleton/Title";

const ConfigurationsScreen: FC = () => {

    const { keycloak } = useKeycloak();
    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const API_BASE = process.env.REACT_APP_API_BASE;

    const [uploading, setUploading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [sendingEmails, setSendingEmails] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);

    const handleUpload = async (file: any) => {
        if (!keycloak || !keycloak.authenticated) {
            openNotification('error', '');
            return false;
        }

        const allowedTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' // .xlsx only
        ];

        if (!allowedTypes.includes(file.type)) {
            openNotification('error', 'Only Excel files (.xlsx) are allowed');
            return false;
        }
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            openNotification('error', 'File size must be less than 10MB');
            return false;
        }

        setUploading(true);

        try {
            await keycloak.updateToken(30);

            const formData = new FormData();
            formData.append('file', file);

            const headers = {
                'Authorization': `Bearer ${keycloak.token}`,
            };

            if(axiosInstance.current) {
                await axiosInstance.current.post('/resource/onboarding/upload-xls', formData, { headers });
            }

            openNotification('customSuccess', 'File uploaded');

            setFileList([]);

        } catch (err) {
            console.error('Upload error:', err);
        } finally {
            setUploading(false);
        }

        return false;
    };

    const handleReset = () => {

        if (axiosInstance.current != null) {
            setResetLoading(true);
            axiosInstance.current
                .post('/resource/admin/reset-database', {})
                .then(() => {
                    openNotification('customSuccess', 'Reset was successfull');
                })
                .catch(function (error) {
                    openNotification('error', error);
                })
                .finally(() => {
                    setResetLoading(false);
                });
        }
    };

    const handleSendInitEmails = async () => {
        if (!keycloak || !keycloak.authenticated) {
            openNotification('error', 'Not authenticated');
            return;
        }

        setSendingEmails(true);

        try {
            await keycloak.updateToken(30);

            const headers = {
                'Authorization': `Bearer ${keycloak.token}`,
                'Content-Type': 'application/json',
            };


            if(axiosInstance.current) {
                await axiosInstance.current.post(`/resource/onboarding/send-init-emails`, {}, { headers });
            }

            openNotification('customSuccess', 'Initialization emails sending was successfully');

        } catch (err) {
            console.error('Send emails error:', err);
        } finally {
            setSendingEmails(false);
        }
    };

    const uploadProps = {
        name: 'file',
        fileList,
        beforeUpload: () => false, // Prevent automatic upload
        onChange: (info: any) => {
            setFileList(info.fileList.slice(-1));
        },
        onRemove: () => {
            setFileList([]);
        },
        accept: '.xlsx',
        maxCount: 1,
    };

    return (
        <>
            <Breadcrumb style={{marginBottom: 10}}>
                <Breadcrumb.Item><Trans id={'Home'}/></Breadcrumb.Item>
                <Breadcrumb.Item><Trans id={'Configurations'}></Trans></Breadcrumb.Item>
            </Breadcrumb>

            <Card
                title={
                    <Space>
                        <FileExcelOutlined />
                        <Trans>Excel File Upload</Trans>
                    </Space>
                }
                style={{ marginBottom: '24px' }}
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <p>
                        <Trans>Upload an Excel file (.xlsx) for onboarding data processing.</Trans>
                    </p>

                    <Dragger {...uploadProps}>
                        <p className="ant-upload-drag-icon">
                            <FileExcelOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                        </p>
                        <p className="ant-upload-text">
                            <Trans>Click or drag Excel file to this area to upload</Trans>
                        </p>
                        <p className="ant-upload-hint">
                            <Trans>
                                Support for .xlsx files only. Maximum file size: 10MB.
                            </Trans>
                        </p>
                    </Dragger>

                    <>
                        <span style={{ marginBottom: '12px', color: '#24292f' }}>
                            <Trans>Excel File Structure Requirements:</Trans>
                        </span>
                        <span style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                            <Trans>Column Order (no headers required):</Trans>
                        </span>
                        <>
                            <span style={{ display: 'block', marginBottom: '4px' }}>
                                <strong>1.</strong> <Trans>Supplier code</Trans>
                            </span>
                            <span style={{ display: 'block', marginBottom: '4px' }}>
                                <strong>2.</strong> <Trans>Supplier email</Trans>
                            </span>
                            <span style={{ display: 'block', marginBottom: '4px' }}>
                                <strong>3.</strong> <Trans>Supplier name</Trans>
                            </span>
                            <span style={{ display: 'block', marginBottom: '4px' }}>
                                <strong>4.</strong> <Trans>Is an EU Supplier (boolean: true/false)</Trans>
                            </span>
                        </>
                        <span style={{
                            display: 'block',
                            marginTop: '12px',
                            fontStyle: 'italic',
                            color: '#656d76'
                        }}>
                            <Trans>Note: Do not include column headers in the Excel file. Start with data from the first row.</Trans>
                        </span>
                    </>

                    <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        loading={uploading}
                        disabled={fileList.length === 0}
                        onClick={() => {
                            if (fileList.length > 0) {
                                handleUpload(fileList[0]['originFileObj']);
                            }
                        }}
                        size="large"
                    >
                        {uploading ? <Trans>Uploading...</Trans> : <Trans>Upload Excel File</Trans>}
                    </Button>
                </Space>
            </Card>

            <Card
                title={
                    <Space>
                        <MailOutlined />
                        <Trans>Email Management</Trans>
                    </Space>
                }
                style={{ marginBottom: '24px' }}
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <p>
                        <Trans>Send initialization emails to users for onboarding process.</Trans>
                    </p>

                    <Button
                        type="primary"
                        icon={<MailOutlined />}
                        loading={sendingEmails}
                        onClick={handleSendInitEmails}
                        size="large"
                    >
                        {sendingEmails ? <Trans>Sending Emails...</Trans> : <Trans>Send Initialization Emails</Trans>}
                    </Button>
                </Space>
            </Card>

            <Card
                title={
                    <Space>
                        <DeleteOutlined />
                        <Trans>Reset My UAT Test data</Trans>
                    </Space>
                }
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <p>
                        <Trans>Delete all data from database</Trans>
                    </p>

                    <Button
                        type="primary"
                        loading={resetLoading}
                        icon={<DeleteOutlined />}
                        onClick={handleReset}
                        size="large"
                    >
                        <Trans>Reset</Trans>
                    </Button>
                </Space>
            </Card>


        </>
    );
}
export default ConfigurationsScreen;
